import React, {useState, useMemo} from "react"
import {Link} from "react-router-dom"
import {useQuery, useQueryClient} from "react-query"
import {toast} from "react-toastify"
import useAuth from "auth/useAuth"
import useAreas from "hooks/useAreas"
import Week from "./Week"
import Wishes from "./Wishes"
import SummerWeek from "./SummerWeek"
import ClosedNotice from "./ClosedNotice"
import "./ShiftApplicationsPage.css"

export default function ShiftApplications() {

    const {area, setArea, mountains} = useAreas()
    const [wish, setWish] = useState(-1)
    const {auth, API, username} = useAuth()
    const queryClient = useQueryClient()
    const {data: weeksData} = useQuery("weeks", () => auth(API.shiftsWeeksGet).then(response => response.data))
    const {data: allApplications, refetch} = useQuery("applications", () => auth(API.shiftsApplicationsGet).then(response => response.data))

    // GSI propagation lag means refetch() right after a write can return stale data.
    // Seed the cache from the server response and reconcile after ~2s.
    const optimisticUpdate = (week, willApply, application) => {
        queryClient.setQueryData("applications", (old) => {
            const list = (old || []).filter(app => !(app.week === week && app.area === area && app.username === username))
            if (willApply && application) list.push(application)
            return list
        })
        setTimeout(() => refetch(), 2000)
    }

    const kind = weeksData?.kind
    const weeks = weeksData?.weeks
    const opensAt = weeksData?.opens_at

    const apply = (wish !== -1)

    const applications = useMemo(() => {
        return allApplications?.filter(app => app.area === area)
    }, [allApplications, area])

    const onWeekClick = (week) => {
        if (!apply)
            return
        const toaster = toast.loading(wish === "0" ? "Odjavljujem..." : "Prijavljujem...")
        auth(API.shiftsApplicationsPut, {"week": week, "wish": wish, "area": area}).then(response => {
            toast.update(toaster, {render: response.data.message, type: "success", isLoading: false, autoClose: 2500})
            // Winter touches multiple rows (wish moves, soft-cancel) so we don't try to
            // predict the result; just delay the refetch past the GSI propagation window.
            setTimeout(() => refetch(), 2000)
        }).catch(response => {
            toast.update(toaster, {render: response.data, type: "error", isLoading: false, autoClose: 2500})
        })
    }

    const onSummerWeekClick = (week) => {
        const wasApplied = allApplications?.some(app => app.week === week && app.area === area && app.username === username)
        const toaster = toast.loading("Snimam...")
        auth(API.shiftsApplicationsPut, {"week": week, "wish": 0, "area": area}).then(response => {
            toast.update(toaster, {render: response.data.message, type: "success", isLoading: false, autoClose: 2500})
            optimisticUpdate(week, !wasApplied, response.data.application)
        }).catch(response => {
            toast.update(toaster, {render: response.data, type: "error", isLoading: false, autoClose: 2500})
        })
    }

    return <div className="container-fluid shadow" id="applications">
        <section id="header" className="white">
            <h3>Prijava smena</h3>
            <select value={area} onChange={event => setArea(event.currentTarget.value)}>
                {mountains.map(area => <option key={area} value={area}>{area}</option>)}
            </select>
        </section>

        {kind === "closed" && <ClosedNotice opensAt={opensAt}/>}

        {kind === "summer" && <>
            <section id="info">
                <details className="instructions">
                    <summary><i className="fa-solid fa-circle-info"/> Uputstvo za letnje prijavljivanje</summary>
                    <ul>
                        <li>Izaberi planinu gore desno</li>
                        <li>Klikni na nedelju da prijaviš ili odjaviš letnju smenu</li>
                        <li>Letnje smene nemaju želje ni bodovanje</li>
                        <li>Slobodno prijavljuj i odjavljuj koliko god želiš</li>
                    </ul>
                </details>
            </section>
            <section id="weeks">
                {weeks && weeks.map(week => <SummerWeek key={week} week={week} applications={applications} onClick={onSummerWeekClick}/>)}
            </section>
        </>}

        {kind === "winter" && <>
            <section id="info">
                <details className="instructions">
                    <summary><i className="fa-solid fa-circle-info"/> Uputstvo za prijavljivanje</summary>
                    <ul>
                        <li>Izaberi planinu gore desno</li>
                        <li>Izaberi želju za prijavljivanje (ili odjavljivanje) dole</li>
                        <li>Klikni na smenu. Nema dugme za snimanje, čim klikneš smena je prijavljena</li>
                        <li>Kada prijaviš smenu, u gornjem desnu uglu za tu nedelju piše koja je tvoja prijavljena želja (zeleni kvadratić)</li>
                        <li>Ako si već stavio želju, pa sa istom željom klikneš na neku drugu nedelju, želja se prebacuje tamo (ako nije već potrošena)</li>
                        <li>Regularno se prijavljuju smene više od tri nedelje unapred (ponoć petak na subotu)</li>
                        <li>Smene manje od tri nedelje unapred <span className="alert alert-warning alert-inline">obeležene žuto</span> mogu se prijaviti za popunjavanje. To znači da se rangiraš ispod onih koji su prijavili na vreme</li>
                        <li>Želju možeš pomerati ako je više od tri nedelje unapred. Kada smena upadne u žuto, možeš samo ispod crte</li>
                        <li>Imaš neograničeno četvrtih želja. One se koriste za popunjavanje, tj. uvek se rangiraju ispod crte</li>
                        <li>To znači da ništa ne dobijaš prijavljivanjem četvrtih želja unapred "za svaki slučaj" (kao što je nekad bilo). Isto se rangira kao da prijaviš nedelju dana unapred</li>
                        <li>Pet dana pred smenu (ponoć nedelja na ponedeljak) završava se prijavljivanje, čak i ispod crte. Nakon toga vidite direktno sa <Link to="/rescuers/tag:koordinator-smena">koordinatorom smena</Link></li>
                        <li>Prijave su sortirane kako treba. Ako klikneš na link "detalji" na vrhu smene, videćeš ko je prijavio sa kojom željom i koliko bodova ima</li>
                        <li><strong>Slobodno prijavljuj i odjavljuj za probu koliko god želiš, da vidiš kako se koristi. Sve što uradiš možeš da poništiš</strong></li>
                    </ul>
                </details>
                <div className="wish-section">
                    <p className="wish-hint">Izaberi želju, pa klikni na smenu</p>
                    <Wishes onChange={setWish} wish={wish}/>
                </div>
            </section>
            <section id="weeks">
                {weeks && weeks.map(week => <Week key={week} week={week} applications={applications} onClick={apply ? onWeekClick : null}/>)}
            </section>
        </>}
    </div>

}
